/**
 * KullaniciParcalari.jsx — Kullanıcılar listesi (KullaniciKart) ile Kullanıcı Kartı'nın ortak küçük parçaları
 * (backlog #379): tarih biçimleri, ad, taraf (Kök / Personel / Firma tarafı), Yerli / Yabancı rozeti, Aktif rozeti, vesikalık,
 * kimlik no (#491).
 *
 * Bu temada `success` gri, `secondary` yeşil basılır (bkz. KisiKullanicilarSekmesi): olumlu rozet `secondary`,
 * renkli kare / şerit ise StandPills'in sabit hex paletinden boyanır.
 *
 * Yerli / Yabancı rozeti backend'in `yabanci` alanıdır (firması varsa firmanın uyruğu, yoksa kullanıcının kendisi);
 * üç kaynak (`uyruk`) birbirini tutmazsa (`uyumsuz`) rozetin yanında küçük bir uyarı işareti durur, kaynaklar ipucundadır.
 *
 * Etiketler `components` ad alanında (`kullaniciParcalari.*`). Render sırasında çağrılan yardımcılar (`adSoyad`, `fuarAdi`) dili
 * çağrı anında okur (`i18n.t`), modül yüklenirken donmaz.
 *
 * WAS (#379): `KullaniciRibbon` — kartın sol üst köşe şeridi (taraf: Kök / Personel / Firma Tarafı / Rolsüz). Deniz 2026-10-26 (#545):
 * liste taraf ayrımını tümüyle bıraktı, şerit kaldırıldı; `tarafMeta` yalnızca Kullanıcı Kartı'nın başlık rozeti ve Rol sekmesi için kaldı.
 */
import React from "react";
import { Badge } from "reactstrap";
import { AlertTriangle } from "lucide-react";
import { useTranslation } from "react-i18next";
import { NEUTRAL_GRAY, STATUS_HEX } from "../Projects/StandPills";
import { PersonelAvatar } from "../Kisi/PersonelKartParcalari";
import useKisiFoto from "../Kisi/useKisiFoto";
import i18n, { currentLocaleTag } from "../../i18n";

/** "2026-10-13T14:30:00" / "2026-10-13 14:30:00" → "13.10.2026 14:30"; çözülemezse olduğu gibi. */
export function tarihSaat(v) {
  if (!v) return null;
  const d = new Date(String(v).replace(" ", "T"));
  return Number.isNaN(d.getTime()) ? String(v) : d.toLocaleString(currentLocaleTag(), { dateStyle: "short", timeStyle: "short" });
}

function tarih(v) {
  if (!v) return null;
  const d = new Date(String(v).replace(" ", "T"));
  return Number.isNaN(d.getTime()) ? String(v) : d.toLocaleDateString(currentLocaleTag());
}

/** Fuarın başlangıç – bitiş aralığı; biri yoksa olanı, ikisi de yoksa null. */
export function tarihAraligi(baslangic, bitis) {
  const a = tarih(baslangic);
  const b = tarih(bitis);
  if (a && b) return `${a} – ${b}`;
  return a || b || null;
}

export const adSoyad = (u) => [u?.userName, u?.userSurname].filter(Boolean).join(" ").trim()
  || u?.userLoginName || i18n.t("components:kullaniciParcalari.kullaniciNo", { id: u?.userId });

/** Liste satırında `userActive` sayı (1 / 0) gelir; boolean'a da dayanıklı. */
export const aktifMi = (u) => u?.userActive === true || Number(u?.userActive) === 1;

/** Kullanıcının tarafı: kök, personel (denetmen rolü) ya da firma tarafı; rolü hiç olmayan ayrı. Etiket: `tarafAdi(taraf)`. */
export function tarafMeta(u) {
  if (u?.root) return { key: "kok", hex: STATUS_HEX.danger };
  if (u?.staff) return { key: "personel", hex: STATUS_HEX.info };
  if (u?.userGroupId == null) return { key: "rolsuz", hex: NEUTRAL_GRAY };
  return { key: "firma", hex: STATUS_HEX.warning };
}

/** `tarafMeta`nın etiketi, çağrı anındaki dilde. */
export const tarafAdi = (taraf) => i18n.t(`components:kullaniciParcalari.taraf.${taraf.key}`);

/** Fuar özetinin (seciliFuar, atandigiFuarlar[i] …) adı; kaydı silinmişse numarasıyla. */
export function fuarAdi(f) {
  if (!f) return "";
  return f.fuarKisaAdi || f.fuarAdi || i18n.t("components:kullaniciParcalari.fuarNo", { id: f.fuarId });
}

const evetHayir = (v) => (v === true
  ? i18n.t("components:kullaniciParcalari.yabanci")
  : v === false ? i18n.t("components:kullaniciParcalari.yerli") : "—");

/** Yerli / Yabancı rozeti (+ kaynaklar uyuşmuyorsa uyarı işareti). `yabanci` yoksa hiçbir şey çizilmez. */
export function UyrukRozeti({ u }) {
  const { t } = useTranslation("components");
  if (u?.yabanci !== true && u?.yabanci !== false) return null;
  const k = u.uyruk;
  const baslik = k
    ? t("kullaniciParcalari.uyrukKaynaklari", {
      firma: evetHayir(k.firmaYabanci), kullanici: evetHayir(k.kullaniciYabanci), kisi: evetHayir(k.kisiYabanci),
    })
    : undefined;
  return (
    <span className="d-inline-flex align-items-center gap-1" title={baslik}>
      {u.yabanci
        ? <Badge color="info-subtle" className="text-info fw-normal">{t("kullaniciParcalari.yabanci")}</Badge>
        : <Badge color="light" className="text-body border fw-normal">{t("kullaniciParcalari.yerli")}</Badge>}
      {k?.uyumsuz && <AlertTriangle size={13} style={{ color: STATUS_HEX.warning }} aria-label={t("kullaniciParcalari.uyrukUyumsuz")} />}
    </span>
  );
}

/** Aktif (yeşil) / Pasif (soluk) rozeti. */
export function AktifRozeti({ u }) {
  const { t } = useTranslation("components");
  return aktifMi(u)
    ? <Badge color="secondary" className="fw-normal">{t("kullaniciParcalari.aktif")}</Badge>
    : <Badge color="light" className="text-body border fw-normal">{t("kullaniciParcalari.pasif")}</Badge>;
}

/**
 * Bağlı kişinin kimlik no / pasaport no'su (backend #490: kişi bağlantısından OKUNUR, kullanıcıda saklanmaz; yalnızca page.kullanici
 * sahibine gelir, bağlı kişi yoksa null). Yabancı kişide ikisi de gelebilir: ikisi de yazılır, yalnızca biri varsa o.
 * Hiçbiri yoksa hiçbir şey çizilmez.
 */
export function KimlikNo({ u, className = "" }) {
  const { t } = useTranslation("components");
  const kimlik = u?.kisi?.kimlikNo;
  const pasaport = u?.kisi?.pasaportNo;
  if (!kimlik && !pasaport) return null;
  return (
    <>
      {kimlik && (
        <span className={className} title={t("kullaniciParcalari.kimlikNoIpucu")}>
          {t("kullaniciParcalari.kimlikNo")}: <span className="font-monospace">{kimlik}</span>
        </span>
      )}
      {pasaport && (
        <span className={className} title={t("kullaniciParcalari.pasaportNoIpucu")}>
          {t("kullaniciParcalari.pasaportNo")}: <span className="font-monospace">{pasaport}</span>
        </span>
      )}
    </>
  );
}

/**
 * Vesikalık: kullanıcının fotoğrafı yoktur, yalnızca bağlı kişininki (`kisi.fotografVar`) görünür; yoksa yer tutucu.
 * Çerçeve = aktiflik (yeşil / gri).
 */
export function KullaniciAvatar({ u, size = 72, onClick, aciklama }) {
  const { t } = useTranslation("components");
  const foto = useKisiFoto(u?.kisi?.personId, Boolean(u?.kisi?.fotografVar));
  const aktif = aktifMi(u);
  return (
    <PersonelAvatar
      photo={foto} size={size} onClick={onClick} aciklama={aciklama}
      cerceve={{ color: aktif ? "success" : "secondary", label: aktif ? t("kullaniciParcalari.aktif") : t("kullaniciParcalari.pasif") }}
    />
  );
}
